import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Save, Loader2, Globe, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

interface PlatformSettings {
  level: 'platform';
  globalSettings?: {
    enabled: boolean;
    defaultFrequency: string;
  };
  channelSettings?: {
    email: { enabled: boolean };
    sms: { enabled: boolean };
  };
}

interface CommunicationSettings {
  level: 'platform';
  smtp?: {
    enabled: boolean;
    host: string;
    port: number;
    auth: {
      user: string;
      pass: string;
    };
  };
}

const PlatformNotificationConfigManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [commSettings, setCommSettings] = useState<CommunicationSettings | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [settingsRes, commRes] = await Promise.all([
        fetch('/api/v1/notifications/config/platform', { headers }),
        fetch('/api/v1/notifications/communication/platform', { headers }),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data.data);
      }
      if (commRes.ok) {
        const data = await commRes.json();
        setCommSettings(data.data);
      }
    } catch (error) {
      toast.error('Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/notifications/config/platform', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(settings),
      });
      if (response.ok) {
        toast.success('Platform settings saved');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComm = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/notifications/communication/platform', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(commSettings),
      });
      if (response.ok) {
        toast.success('Communication settings saved');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 mb-2">
          <Shield className="h-6 w-6 text-primary-600" />
          <h1 className="text-3xl font-bold">Platform Notification Configuration</h1>
        </div>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Globe className="h-4 w-4" />
        <AlertDescription className="ml-2">
          These settings apply platform-wide. Tenants can override them in their settings.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 gap-6">
        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
            <p className="text-sm text-gray-600">Configure default notification behavior</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Enable Notifications</Label>
              <div className="mt-2 flex items-center space-x-4">
                <select
                  className="w-full border rounded px-3 py-2"
                  value={settings?.globalSettings?.enabled ? 'true' : 'false'}
                  onChange={(e) =>
                    setSettings({
                      ...settings!,
                      globalSettings: {
                        ...settings?.globalSettings!,
                        enabled: e.target.value === 'true',
                      },
                    })
                  }
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </CardContent>
        </Card>

        {/* SMTP Settings */}
        {commSettings && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="h-5 w-5" />
                <span>SMTP Configuration</span>
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">Configure platform-wide SMTP settings</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Host</Label>
                  <Input
                    value={commSettings.smtp?.host || ''}
                    onChange={(e) =>
                      setCommSettings({
                        ...commSettings,
                        smtp: { ...commSettings.smtp!, host: e.target.value },
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={commSettings.smtp?.port || 587}
                    onChange={(e) =>
                      setCommSettings({
                        ...commSettings,
                        smtp: { ...commSettings.smtp!, port: parseInt(e.target.value) },
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={commSettings.smtp?.auth?.user || ''}
                    onChange={(e) =>
                      setCommSettings({
                        ...commSettings,
                        smtp: {
                          ...commSettings.smtp!,
                          auth: { ...commSettings.smtp?.auth!, user: e.target.value },
                        },
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={commSettings.smtp?.auth?.pass || ''}
                    onChange={(e) =>
                      setCommSettings({
                        ...commSettings,
                        smtp: {
                          ...commSettings.smtp!,
                          auth: { ...commSettings.smtp?.auth!, pass: e.target.value },
                        },
                      })
                    }
                    className="mt-1"
                  />
                </div>
              </div>
              <Button onClick={handleSaveComm} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PlatformNotificationConfigManagement;
